import dayjs from 'dayjs';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { RotateLoader } from 'react-spinners';
import { HandleError } from '../Common/ErrorHandling/ErrorHelper';
import { FilterAutocompleteBox } from '../Common/FilterPopover';

import TableBox, { TableBoxColumn } from '../Common/Table/TableBox';
import { useNotify } from '../Common/snackbarHooks';

import { stringToPositiveInt, useQuery } from '../Common/utilities';
import { getRegisterControllers, getUsersMinimalList } from '../User/usersApi';
import AuthContext from '../Common/AuthContext';
import { AuthContextValue, OptionsInterface } from '../Common/types';
import { deleteRegisterRequest, findRegisterRequests } from './registerRequestApi';
import { getResidenceGroupsMinimalList } from '../ResidenceGroup/residenceGroupApi';
import { RegisterRequestInterface } from '../RegisterUpload/types';
import FilterDatePicker from '../Common/FilterPopover/FilterDatePicker';
import RegisterRequestsSearchBar from './components/SearchBar';
import RegisterRequestsFilterPopover from './components/FilterPopover';
import { MenuItem, Tooltip } from '@mui/material';
import { AutoDeleteOutlined, DeleteOutline, DescriptionOutlined, Add } from '@mui/icons-material';
import { Fab } from '@mui/material';
import CreateRegisterRequestDialog from './components/CreateRegisterRequestDialog';
import { MenuTableCell } from '../Common/Table';
import { QuickAction } from '../Common/ButtonLinks';
import ConfirmationDialog from '../Common/Dialogs/ConfirmationDialog';
import { commonStyles } from '../Common/styling';

const tableColumns: TableBoxColumn[] = [
  { id: 'name', label: 'Namn', width: 'xl', lineStyle: 'compress' },
  { id: 'created_at', label: 'Skapad', width: 'xs' },
  { id: 'assessment', label: 'Status', width: 'xs' },
  { id: 'valid_from', label: 'Giltig från', width: 'xs' },
  { id: 'valid_to', label: 'Giltig till', width: 'xs' },
  { id: 'assessment_at', label: 'Granskad', width: 'xs' },
  { id: 'reason', label: 'Orsak', width: 'xs' },
  { id: 'assessment_by', label: 'Granskad av', width: 'xl', lineStyle: 'compress' },
  { id: 'files', label: '', width: 'xs', sortable: false },
];

const PAGE_SIZE = 25;

export default function RegisterRequests() {
  const { notifySuccess, notifyError } = useNotify();
  const location = useLocation();

  const [registerRequests, setRegisterRequests] = useState<RegisterRequestInterface[]>([]);
  const [operationalManagers, setOperationalManagers] = useState<OptionsInterface[]>([]);
  const [residenceGroups, setResidenceGroups] = useState<OptionsInterface[]>([]);
  const [rcUsers, setRcUsers] = useState<OptionsInterface[]>([]);
  const [registerControllers, setRegisterControllers] = useState<OptionsInterface[]>([]);
  const [totalRegisterRequests, setTotalRegisterRequests] = useState<number>();
  const [loading, setLoading] = useState(true);
  const [loadingOperationalManagers, setLoadingOperationalManagers] = useState(true);
  const [loadingResidenceGroups, setLoadingResidenceGroups] = useState(true);
  const [loadingRcUsers, setLoadingRcUsers] = useState(true);
  const [loadingRegisterControllers, setLoadingRegisterControllers] = useState(true);
  const [query, setQuery] = useQuery(
    {
      search: (val) => val,
      operationalManagers: (val) => val,
      residenceGroups: (val) => val,
      toDate: (val) => (dayjs(val, 'YYYY-MM-DD').isValid() ? dayjs(val, 'YYYY-MM-DD') : undefined),
      showNotHandled: (val) => (val === 'true' ? true : val === 'false' ? false : undefined),
      showRequiresCompletion: (val) => (val === 'true' ? true : val === 'false' ? false : undefined),
      showUnderInvestigation: (val) => (val === 'true' ? true : val === 'false' ? false : undefined),
      showApproved: (val) => (val === 'true' ? true : val === 'false' ? false : undefined),
      showExceptionValidPrevious: (val) => (val === 'true' ? true : val === 'false' ? false : undefined),
      showExceptionUnderFourteen: (val) => (val === 'true' ? true : val === 'false' ? false : undefined),
      showExceptionNotRequired: (val) => (val === 'true' ? true : val === 'false' ? false : undefined),
      showExceptionMigration: (val) => (val === 'true' ? true : val === 'false' ? false : undefined),
      showComplete: (val) => (val === 'true' ? true : val === 'false' ? false : undefined),
      sortBy: (val) => val,
      sortOrder: (val) => (val === 'asc' ? ('asc' as const) : val === 'desc' ? ('desc' as const) : undefined),
      page: (val) => stringToPositiveInt(val),
    },
    { backConverter: { toDate: (val) => val?.format('YYYY-MM-DD') } }
  );

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const { hasPermissions } = useContext(AuthContext) as AuthContextValue;

  const pageTopRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchRegisterRequests();
  }, [query]);

  useEffect(() => {
    fetchUsers();
    fetchResidenceGroups();
    if (hasPermissions(['admin', 'operational_leader'])) {
      fetchRcUsers();
      fetchRegisterControllers();
    }
  }, []);

  const fetchRegisterRequests = (): void => {
    setLoading(true);
    findRegisterRequests({
      query: query.search,
      page: query.page ?? 1,
      filter_operational_managers: query.operationalManagers?.split(',').map((id) => id),
      filter_residence_groups: query.residenceGroups?.split(',').map((id) => stringToPositiveInt(id)),
      filter_to_date: query.toDate?.format('YYYY-MM-DD'),
      show_complete: query.showComplete ?? true,
      show_not_handled: query.showNotHandled ?? true,
      show_requires_completion: query.showRequiresCompletion ?? false,
      show_under_investigation: query.showUnderInvestigation ?? false,
      show_approved: query.showApproved ?? false,
      show_exception_valid_previous: query.showApproved ? query.showExceptionValidPrevious ?? true : false,
      show_exception_under_fourteen: query.showApproved ? query.showExceptionUnderFourteen ?? true : false,
      show_exception_not_required: query.showApproved ? query.showExceptionNotRequired ?? true : false,
      show_exception_migration: query.showApproved ? query.showExceptionMigration ?? true : false,
      sort_by: query.sortBy,
      sort_order: query.sortOrder,
    })
      .then(({ data }) => {
        setRegisterRequests(data.data);
        setTotalRegisterRequests(data.meta.count);
        pageTopRef.current?.scrollIntoView();
      })
      .finally(() => setLoading(false));
  };

  const fetchUsers = (): void => {
    getUsersMinimalList({ hide_banned: true, filter_by_role_name: 'operational_manager' })
      .then(({ data }) => setOperationalManagers(data.data))
      .catch(() => notifyError('Det gick inte att hämta listan över VA.'))
      .finally(() => setLoadingOperationalManagers(false));
  };

  const fetchResidenceGroups = (): void => {
    getResidenceGroupsMinimalList({ is_active: true })
      .then(({ data }) => setResidenceGroups(data.data))
      .catch(() => notifyError('Det gick inte att hämta listan över avtalspart.'))
      .finally(() => setLoadingResidenceGroups(false));
  };

  const fetchRcUsers = (): void => {
    getUsersMinimalList({ hide_banned: true, filter_by_role_name: 'regional_manager' })
      .then(({ data }) => setRcUsers(data.data))
      .catch(() => notifyError('Det gick inte att hämta listan över regionala chefer.'))
      .finally(() => setLoadingRcUsers(false));
  };

  const fetchRegisterControllers = (): void => {
    getRegisterControllers()
      .then(({ data }) => setRegisterControllers(data.data))
      .catch(() => notifyError('Det gick inte att hämta listan över registerkontrollanter.'))
      .finally(() => setLoadingRegisterControllers(false));
  };

  function removeRegisterRequest() {
    if (deleteId === null) return;
    deleteRegisterRequest(deleteId)
      .then(() => {
        notifySuccess('RegU-begäran har tagits bort.');
        fetchRegisterRequests();
        setDeleteId(null);
      })
      .catch((error) => notifyError(`Det gick inte att ta bort RegU-begäran: ${HandleError(error.response.data)}`));
  }

  function updateSorting(col: string) {
    return setQuery({ sortBy: col, sortOrder: query.sortBy !== col || query.sortOrder === 'desc' ? 'asc' : 'desc' });
  }

  function assessmentToText(rr: RegisterRequestInterface): string {
    if (
      rr.assessment === 'Godkänd' &&
      rr.category &&
      [
        'Undantagen (giltig RU)',
        'Undantagen (under 14)',
        'Undantagen (under 15)',
        'Undantagen (avtalspart)',
        'Undantagen (migration)',
      ].includes(rr.category)
    ) {
      return rr.category;
    } else {
      return rr.assessment;
    }
  }

  return (
    <div ref={pageTopRef} className="min-h-full">
      {/* Title */}
      <div className="h-16 p-4 flex items-center justify-between bg-gray-100">
        <h1 className="text-2xl font-semibold">Registerkontroll</h1>
      </div>
      {/* Filters */}
      <div className="bg-white flex h-13">
        <RegisterRequestsSearchBar
          value={query.search}
          onChange={(value) => setQuery({ search: value || undefined })}
        />
        <div className="flex">
          <FilterAutocompleteBox
            multiple
            renderTags="simple"
            options={operationalManagers}
            value={
              operationalManagers.filter((r) => query.operationalManagers?.split(',').includes(r.id.toString())) ?? null
            }
            onChange={(_, options) => {
              setQuery({
                operationalManagers:
                  Array.isArray(options) && options.length > 0
                    ? options.map((r: OptionsInterface) => r.id).join(',')
                    : undefined,
                page: undefined,
              });
            }}
            placeholder="Sök VA"
            getOptionLabel={(option) => option.name}
            clearable
            loading={loadingOperationalManagers}
            className="w-48 2xl:w-64"
          />
          <FilterAutocompleteBox
            multiple
            renderTags="simple"
            options={residenceGroups}
            value={residenceGroups.filter((r) => query.residenceGroups?.split(',').includes(r.id.toString())) ?? null}
            onChange={(_, options) =>
              setQuery({
                residenceGroups: Array.isArray(options)
                  ? options.map((r: OptionsInterface) => r.id).join(',')
                  : undefined,
                page: undefined,
              })
            }
            placeholder="Sök avtalspart"
            getOptionLabel={(option) => option.name}
            loading={loadingResidenceGroups}
            clearable
            className="w-48 2xl:w-64"
          />
          <FilterDatePicker
            views={['year', 'month', 'day']}
            value={query.toDate ?? dayjs()}
            onChange={(date) => setQuery({ toDate: date?.format('YYYY-MM-DD') ?? undefined, page: undefined })}
            placeholder="Välj datum"
            className="w-48 2xl:w-64"
            actions={['clear']}
          />
          <RegisterRequestsFilterPopover
            rcVaUsers={[...rcUsers, ...operationalManagers]}
            loadingRcUsers={loadingRcUsers}
            loadingOperationalManagers={loadingOperationalManagers}
            registerControllers={registerControllers}
            setRegisterControllers={setRegisterControllers}
            loadingRegisterControllers={loadingRegisterControllers}
            showNotHandled={query.showNotHandled}
            setShowNotHandled={(val) => setQuery({ showNotHandled: val, page: undefined })}
            showRequiresCompletion={query.showRequiresCompletion}
            setShowRequiresCompletion={(val) => setQuery({ showRequiresCompletion: val, page: undefined })}
            showUnderInvestigation={query.showUnderInvestigation}
            setShowUnderInvestigation={(val) => setQuery({ showUnderInvestigation: val, page: undefined })}
            showApproved={query.showApproved}
            setShowApproved={(val) => {
              setQuery({
                showApproved: val,
                showExceptionValidPrevious: val ? true : undefined,
                showExceptionUnderFourteen: val ? true : undefined,
                showExceptionNotRequired: val ? true : undefined,
                showExceptionMigration: val ? true : undefined,
                page: undefined,
              });
            }}
            showExceptionValidPrevious={query.showApproved ? query.showExceptionValidPrevious ?? true : false}
            setShowExceptionValidPrevious={(val) => setQuery({ showExceptionValidPrevious: val, page: undefined })}
            showExceptionUnderFourteen={query.showApproved ? query.showExceptionUnderFourteen ?? true : false}
            setShowExceptionUnderFourteen={(val) => setQuery({ showExceptionUnderFourteen: val, page: undefined })}
            showExceptionNotRequired={query.showApproved ? query.showExceptionNotRequired ?? true : false}
            setShowExceptionNotRequired={(val) => setQuery({ showExceptionNotRequired: val, page: undefined })}
            showExceptionMigration={query.showApproved ? query.showExceptionMigration ?? true : false}
            setShowExceptionMigration={(val) => setQuery({ showExceptionMigration: val, page: undefined })}
            showComplete={query.showComplete}
            setShowComplete={(val) => setQuery({ showComplete: val, page: undefined })}
          />
        </div>
      </div>
      {/* Content */}
      <TableBox
        columns={tableColumns}
        content={registerRequests ?? []}
        rowDefinition={(rr: RegisterRequestInterface) => ({
          key: rr.id,
          defaultLink: `/registry-control/${rr.id}${location.search}`,
          cols: [
            `${rr.user.first_name} ${rr.user.surname} (nr ${rr.user.employment_nr})`,
            {
              content: (
                <span className={rr.unapproved_within_12_weeks ? 'font-bold text-red-600' : ''}>{rr.created_at}</span>
              ),
            },
            assessmentToText(rr),
            rr.valid_from,
            rr.valid_to,
            rr.assessment_at,
            rr.reason,
            rr.assessment_by?.name,
            {
              padding: 'none',
              content: (
                <Tooltip
                  title={
                    rr.uploads_with_file > 0
                      ? 'Begäran har uppladdade filer'
                      : rr.uploads_without_file > 0
                      ? 'Begäran har raderade filer'
                      : 'Inga uppladdade filer'
                  }
                  placement="top-start"
                >
                  <div className="w-full inline-flex items-center p-4">
                    <span className="mr-2.5">
                      {rr.uploads_with_file > 0 ? (
                        <DescriptionOutlined />
                      ) : rr.uploads_without_file > 0 ? (
                        <AutoDeleteOutlined sx={{ opacity: 0.3 }} />
                      ) : null}
                    </span>
                  </div>
                </Tooltip>
              ),
            },
            {
              content: (
                <MenuTableCell>
                  <MenuItem className="!p-0">
                    <QuickAction
                      onClick={() => setDeleteId(rr.id)}
                      text="Ta bort"
                      icon={<DeleteOutline className="!mr-2.5" />}
                      label="delete_register_request"
                      styles={commonStyles.selectionFabStyle3}
                    />
                  </MenuItem>
                </MenuTableCell>
              ),
              customCell: true,
            },
          ],
        })}
        pagination={{
          totalEntries: totalRegisterRequests ?? 0,
          pageSize: PAGE_SIZE,
          page: query.page ?? 1,
          onChange: (_, page) => setQuery({ page }),
        }}
        sorting={{ sortBy: query.sortBy, sortOrder: query.sortOrder, handleSortChange: updateSorting }}
        moreActionsCol
        fullWidth
      />

      {hasPermissions(['admin', 'operational_leader']) && (
        <Fab
          color="primary"
          className="!fixed !bottom-8 !right-8"
          aria-label="Add"
          onClick={(): void => setCreateDialogOpen(true)}
        >
          <Add />
        </Fab>
      )}

      <ConfirmationDialog
        isVisible={deleteId !== null}
        title="Ta bort RegU-begäran"
        message="Är du säker på att du vill ta bort denna RegU-begäran?"
        onClose={() => setDeleteId(null)}
        onConfirm={removeRegisterRequest}
      />

      <CreateRegisterRequestDialog
        isVisible={createDialogOpen}
        onClose={(): void => {
          setCreateDialogOpen(false);
        }}
      />

      {loading && (
        <div className="fixed top-1/2 left-1/2">
          <RotateLoader />
        </div>
      )}
    </div>
  );
}
